import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Eye, EyeOff, Loader2 } from "lucide-react";

const LoginPage = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error("Please fill in all fields");
      return;
    }

    setLoading(true);
    try {
      const data = await login(email, password);
      toast.success(`Welcome back, ${data.user.name}`);
      
      switch(data.user.role) {
        case 'admin':
          navigate('/admin');
          break;
        case 'staff':
          navigate('/staff');
          break;
        default:
          navigate('/dashboard');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex">
      {/* Left side - Luxury Branding */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden hero-pattern">
        <div className="absolute inset-0 bg-gradient-to-br from-[#111111] via-[#0A0A0A] to-[#111111]" />
        
        {/* Decorative elements */}
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-[#C9A962]/30 to-transparent" />
        <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-[#C9A962]/30 to-transparent" />
        
        {/* Glowing orbs */}
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[#C9A962]/5 rounded-full blur-3xl animate-float" />
        <div className="absolute bottom-1/4 right-1/4 w-64 h-64 bg-[#C9A962]/3 rounded-full blur-3xl" style={{animationDelay: '3s'}} />
        
        <div className="relative z-10 flex flex-col justify-center px-16 xl:px-24">
          {/* Logo */}
          <div className="mb-12">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-16 h-16 rounded-full border-2 border-[#C9A962]/30 flex items-center justify-center">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#C9A962] to-[#A68B4B]" />
              </div>
              <div>
                <h1 className="text-4xl font-display font-medium tracking-wide">
                  <span className="text-gradient-luxury">OCULUS</span>
                </h1>
                <p className="text-[#C9A962] text-sm tracking-[0.3em] font-elegant">ROYALE</p>
              </div>
            </div>
          </div>
          
          <h2 className="text-5xl font-display font-medium text-white mb-6 leading-tight">
            The Art of<br />
            <span className="text-[#C9A962]">Exceptional</span> Service
          </h2>
          
          <p className="text-[#9A9A9A] text-lg font-light mb-12 max-w-md leading-relaxed">
            Experience luxury redefined. Every mile earned brings you closer to extraordinary rewards.
          </p>
          
          {/* Features */}
          <div className="space-y-6">
            <div className="flex items-center gap-5">
              <div className="w-12 h-12 rounded-lg bg-[#C9A962]/10 border border-[#C9A962]/20 flex items-center justify-center">
                <svg className="w-5 h-5 text-[#C9A962]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <h3 className="text-white font-medium tracking-wide">Earn Miles</h3>
                <p className="text-[#5A5A5A] text-sm">Every journey rewards you</p>
              </div>
            </div>
            
            <div className="flex items-center gap-5">
              <div className="w-12 h-12 rounded-lg bg-[#C9A962]/10 border border-[#C9A962]/20 flex items-center justify-center">
                <svg className="w-5 h-5 text-[#C9A962]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                </svg>
              </div>
              <div>
                <h3 className="text-white font-medium tracking-wide">Exclusive Rewards</h3>
                <p className="text-[#5A5A5A] text-sm">Curated privileges await</p>
              </div>
            </div>
            
            <div className="flex items-center gap-5">
              <div className="w-12 h-12 rounded-lg bg-[#C9A962]/10 border border-[#C9A962]/20 flex items-center justify-center">
                <svg className="w-5 h-5 text-[#C9A962]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                </svg>
              </div>
              <div>
                <h3 className="text-white font-medium tracking-wide">Elite Status</h3>
                <p className="text-[#5A5A5A] text-sm">Silver • Gold • Platinum</p>
              </div>
            </div>
          </div>
        </div>
        
        {/* Bottom tagline */}
        <div className="absolute bottom-8 left-16 xl:left-24">
          <p className="text-[#5A5A5A] text-xs tracking-[0.2em] uppercase">Crafted for the discerning few</p>
        </div>
      </div>

      {/* Right side - Login Form */}
      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden text-center mb-12">
            <div className="inline-flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full border border-[#C9A962]/30 flex items-center justify-center">
                <div className="w-7 h-7 rounded-full bg-gradient-to-br from-[#C9A962] to-[#A68B4B]" />
              </div>
              <div className="text-left">
                <h1 className="text-2xl font-display font-medium text-gradient-luxury">OCULUS</h1>
                <p className="text-[#C9A962] text-xs tracking-[0.2em] font-elegant">ROYALE</p>
              </div>
            </div>
          </div>

          <div className="luxury-card p-8 md:p-10">
            <div className="mb-8">
              <h2 className="text-2xl font-display font-medium text-white mb-2">Welcome Back</h2>
              <p className="text-[#5A5A5A] text-sm">Sign in to access your exclusive benefits</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-[#9A9A9A] text-xs uppercase tracking-wider">Email Address</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="your@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="bg-[#1A1A1A] border-[#252525] text-white placeholder:text-[#5A5A5A] focus:border-[#C9A962] focus:ring-[#C9A962]/20 h-12"
                  data-testid="login-email-input"
                />
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password" className="text-[#9A9A9A] text-xs uppercase tracking-wider">Password</Label>
                  <Link to="/password-reset" className="text-[#C9A962] hover:text-[#D4B978] text-xs transition-colors">
                    Forgot Password?
                  </Link>
                </div>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="bg-[#1A1A1A] border-[#252525] text-white placeholder:text-[#5A5A5A] focus:border-[#C9A962] focus:ring-[#C9A962]/20 h-12 pr-10"
                    data-testid="login-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-[#5A5A5A] hover:text-[#9A9A9A] transition-colors"
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full h-12 bg-gradient-to-r from-[#C9A962] to-[#A68B4B] hover:opacity-90 text-[#0A0A0A] font-semibold text-xs tracking-widest uppercase"
                data-testid="login-submit-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Authenticating...
                  </>
                ) : (
                  "Sign In"
                )}
              </Button>
            </form>

            <div className="luxury-divider my-8">or</div>

            <div className="text-center">
              <p className="text-[#5A5A5A] text-sm">
                New to Oculus Royale?{" "}
                <Link to="/register" className="text-[#C9A962] hover:text-[#D4B978] transition-colors" data-testid="register-link">
                  Create Account
                </Link>
              </p>
            </div>
          </div>
          
          {/* Footer */}
          <p className="text-center text-[#5A5A5A] text-xs mt-8 tracking-wide">
            By signing in, you agree to our Terms of Service
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
